import React, { useState, useEffect } from 'react';
import { useBilling } from './useBillings';
import { BILLINGS } from '../../constants/columns';
import CustomContainer from '../../common/custom/CustomContainer';
import CustomTable from '../../common/custom/CustomTable/custom-table.component';
import {
  ClientsPanelHeader,
  CustomButton,
  CustomEmptyDataComponent,
  CustomHeading,
} from '../../common/custom';
import FilterBillingModal from './filterBilling';
import { crossCircle, filterIcon } from '../../assets';
import DownloadCSVButton from '../../common/custom/download-csv';
import useApi from '../../hook/useApi';
import moment from 'moment';
import { FORMATE_AMOUNT } from '../../helpers/helpers';
import { fetchAllTradeEvents } from '../../api/services/tradeShowPolicy.service';

// Define tradeshow columns
const TRADESHOW_COLUMNS = [
  { id: 1, title: 'Event Name', name: 'name' },
  { id: 2, title: 'Start Date', name: 'date' },
  { id: 3, title: 'End Date', name: 'deadline' },
  { id: 4, title: 'Venue', name: 'venue' },
  { id: 5, title: 'Premium', name: 'premium' },
  { id: 6, title: 'Paid', name: 'paid' },
  { id: 7, title: 'Plans', name: 'plans' },
  { id: 8, title: 'Status', name: 'status' },
  { id: 9, title: 'Refund', name: 'refund' },
];

function parseJwt(token) {
  try {
    // Split the token into its 3 parts (header, payload, signature)
    const base64Url = token.split('.')[1];

    // Replace characters to make it base64 compatible
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

    // Decode and parse the JSON
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decoding JWT:', e);
    return null;
  }
}

const Biillings = () => {
  const [activeTab, setActiveTab] = useState('shipments');
  const token = localStorage.getItem('token');
  const decodedToken = parseJwt(token);

  // Filter out reward column if isRebate is false
  const filteredBillings = decodedToken?.isRebate
    ? BILLINGS
    : BILLINGS.filter((column) => column.name !== 'reward');

  // Get tradeshow data
  const [getAllTradeEvents, allTradeEventsLoading, , allTradeEventsData] =
    useApi(fetchAllTradeEvents);

  useEffect(() => {
    if (activeTab === 'tradeshow') {
      getAllTradeEvents();
    }
  }, [activeTab]);

  // Process tradeshow data
  const tradeEvents =
    allTradeEventsData?.AllTradeEvents?.map((item) => ({
      ...item,
      plans: item?.subscriptionPlan?.name,
      date: moment(item?.startDate).format('MM/DD/YY'),
      deadline: moment(item?.endDate).format('MM/DD/YY'),
      paid: item?.isPaid ? 'Yes' : 'No',
      premium: FORMATE_AMOUNT(item?.amountPaid),
      refund: FORMATE_AMOUNT(item?.refundAmount),
      status: item?.status || 'Pending',
    })) || [];

  const {
    data,
    loading,
    showFilterModal,
    handleOpenFilterModal,
    handleCloseFilterModal,
    handleFilterSubmit,
    handleFilterReset,
    handleSubmit,
    register,
    errors,
    setValue,
    isFilterCleared,
    setIsFilterCleared,
  } = useBilling();

  // Determine which data to show based on active tab
  const activeData = activeTab === 'shipments' ? data : tradeEvents;
  const activeColumns =
    activeTab === 'shipments' ? filteredBillings : TRADESHOW_COLUMNS;
  const isLoading = activeTab === 'shipments' ? loading : allTradeEventsLoading;

  return (
    <CustomContainer>
      {showFilterModal && activeTab === 'shipments' && (
        <FilterBillingModal
          closeFilterModal={handleCloseFilterModal}
          onReset={handleFilterReset}
          errors={errors}
          register={register}
          setValue={setValue}
          handleSubmit={handleSubmit}
          onSubmit={handleFilterSubmit}
          setIsFilterCleared={setIsFilterCleared}
        />
      )}

      <ClientsPanelHeader hideReportButton />
      <div className='border-2 border-[#EAECF0] rounded-[7px]'>
        <CustomContainer className='px-6 min-h-[75px] flex justify-center items-center'>
          <div className='flex justify-between items-center flex-row w-full'>
            <div className='flex flex-col w-full'>
              <CustomHeading className='text-foundation-black mt-2 font-inter text-small-heading font-[600] max-w-[1152px] w-full'>
                Billings
              </CustomHeading>

              {/* Tabs */}
              <div className='flex mt-4 border-b border-[#EAECF0]'>
                <button
                  className={`py-2 px-4 font-medium ${
                    activeTab === 'shipments'
                      ? 'text-[#77553D] border-b-2 border-[#77553D]'
                      : 'text-[#667085]'
                  }`}
                  onClick={() => setActiveTab('shipments')}
                >
                  Shipments
                </button>
                <button
                  className={`py-2 px-4 font-medium ${
                    activeTab === 'tradeshow'
                      ? 'text-[#77553D] border-b-2 border-[#77553D]'
                      : 'text-[#667085]'
                  }`}
                  onClick={() => setActiveTab('tradeshow')}
                >
                  Trade Show
                </button>
              </div>
            </div>

            <div className='flex justify-between gap-4 w-[20%]'>
              {activeData?.length > 0 && (
                <DownloadCSVButton
                  data={activeData}
                  filename={
                    activeTab === 'shipments'
                      ? 'account-billings'
                      : 'tradeshow-events'
                  }
                />
              )}

              {!isLoading &&
                activeTab === 'shipments' &&
                (isFilterCleared ? (
                  activeData?.length > 0 && (
                    <CustomButton
                      onClick={handleOpenFilterModal}
                      className='px-[10px] py-[16px] text-[14px] font-bold font-[inter] text-[#101828] flex items-center h-[48px] gap-2 text-[#344054] border-2 border-[#D0D5DD] rounded-[7px]'
                      text='Filter'
                      icon={filterIcon}
                    />
                  )
                ) : (
                  <CustomButton
                    onClick={() => setIsFilterCleared(true)}
                    className='px-[10px] py-[16px] text-[14px] font-bold font-[inter] min-w-[140px] text-[#101828] flex items-center h-[48px] gap-2 text-[#344054] border-2 border-[#D0D5DD] rounded-[7px]'
                    text='Clear Filters'
                    icon={crossCircle}
                  />
                ))}
            </div>
          </div>
        </CustomContainer>

        <div className='overflow-x-auto'>
          {activeData?.length ? (
            <CustomTable
              header={activeColumns}
              rows={activeData}
              table={activeTab === 'shipments' ? 'billing' : 'tradeshow'}
            />
          ) : isLoading ? (
            <CustomEmptyDataComponent
              heading='Loading data...'
              loading={true}
            ></CustomEmptyDataComponent>
          ) : (
            <CustomEmptyDataComponent heading='No Data Found!'></CustomEmptyDataComponent>
          )}
        </div>
      </div>
    </CustomContainer>
  );
};

export default Biillings;
